import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

export type CampaignDayStatus = "not_started" | "running" | "complete" | "partial" | "blocked" | "failed";
export type CampaignDayStep = {
  step: "source_checks" | "evidence_packet";
  status: "not_started" | "running" | "complete" | "partial" | "blocked" | "failed" | "skipped" | "no_action";
  safe_error_code: string | null;
  evidence_packet_id?: string;
  reason: string | null;
  updated_at: string | null;
};
export type CampaignDaySource = {
  source_id: string; status: "healthy" | "stale" | "partial" | "disconnected" | "permission_denied" | "unsupported" | "unknown";
  observed_at: string | null; requested_window: { starts_at: string | null; ends_at: string | null };
  covered_window: { starts_at: string | null; ends_at: string | null } | null; snapshot_ref: string | null; note: string | null;
};
export type CampaignDay = {
  day_id: string; campaign_id: string; plan_id: string; plan_revision: number; local_date: string; timezone: string;
  day_revision: number; status: CampaignDayStatus; ledger_revision: number;
  ledger: { sources: CampaignDaySource[]; evidence_packets?: Array<{ packet_id: string; as_of: string; sources: CampaignDaySource[] }>;
    steps: CampaignDayStep[]; next_action: string | null; as_of: string };
  created_at: string; updated_at: string;
};
type DayPage = { items: CampaignDay[]; total: number; next_offset: number | null };
export type DailyLoopReadiness = {
  contract_version: 1; campaign_id: string; brand_id: string; settings_revision: number; as_of: string;
  status: "ready" | "partial" | "blocked" | "not_configured"; review_only: true;
  resources: Array<{ reference_id: string; kind: string; label: string; status: string; observed_at: string | null;
    requested_window: { starts_at: string | null; ends_at: string | null };
    covered_window: { starts_at: string | null; ends_at: string | null } | null; setup_path: string | null; detail: string }>;
};
const base = (brandId: string, campaignId: string) => `/api/v1/brands/${encodeURIComponent(brandId)}/campaigns/${encodeURIComponent(campaignId)}`;
function assertScope<T extends { campaign_id: string }>(value: T, campaignId: string): T {
  if (value.campaign_id.toLowerCase() !== campaignId.toLowerCase()) throw new Error("Campaign daily work returned a different campaign.");
  return value;
}
export function useCampaignDays(brandId?: string, campaignId?: string) {
  return useQuery({ queryKey: ["campaign-days", brandId, campaignId], enabled: !!brandId && !!campaignId, retry: false,
    queryFn: async () => {
      const response = await api.get<{ data: DayPage }>(`${base(brandId!, campaignId!)}/days?limit=20&offset=0`);
      return { data: { ...response.data, items: response.data.items.map(day => assertScope(day, campaignId!)) } };
    } });
}
export function useCampaignDailyLoopReadiness(brandId?: string, campaignId?: string) {
  return useQuery({ queryKey: ["campaign-daily-loop-readiness", brandId, campaignId], enabled: !!brandId && !!campaignId, retry: false,
    queryFn: async () => {
      const response = await api.get<{ data: DailyLoopReadiness }>(`${base(brandId!, campaignId!)}/daily-loop/readiness`);
      if (response.data.campaign_id.toLowerCase() !== campaignId!.toLowerCase() || response.data.brand_id.toLowerCase() !== brandId!.toLowerCase())
        throw new Error("Campaign readiness returned a different brand or campaign.");
      return response;
    } });
}
